import React, { createContext, useContext, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUserStore } from '../stores/userStore';

interface User {
  id: string;
  email: string;
  role: string;
  isPremium: boolean;
  fullName?: string;
  phone?: string;
  photo?: string;
  occupation?: string;
  location?: string;
  languages?: string[];
  aboutMe?: string;
  joinDate?: string;
  verifications?: {
    id?: {
      status: 'pending' | 'verified' | 'rejected';
      type: string;
    };
    address?: {
      status: 'pending' | 'verified' | 'rejected';
      type: string;
    };
  };
}

interface AuthContextType {
  isAuthenticated: boolean;
  isPremium: boolean;
  user: User | null;
  login: (email: string, password: string) => boolean;
  register: (email: string, password: string, role: string) => void;
  logout: () => void;
  upgradeToPremium: () => void;
  isEmailRegistered: (email: string) => boolean;
  updateUserProfile: (data: Partial<User>) => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isPremium, setIsPremium] = useState(false);
  const [user, setUser] = useState<User | null>(null);
  const { users, addUser, updateUser } = useUserStore();

  const login = (email: string, password: string): boolean => {
    const foundUser = users[email];
    if (foundUser) {
      setUser(foundUser);
      setIsAuthenticated(true);
      setIsPremium(foundUser.isPremium);
      return true;
    }
    return false;
  };

  const register = (email: string, password: string, role: string) => {
    const newUser: User = {
      id: email,
      email,
      role,
      isPremium: false,
      joinDate: new Date().toISOString(),
      verifications: {}
    };
    
    addUser(email, newUser);
    setUser(newUser);
    setIsAuthenticated(true);
    setIsPremium(false);
  };

  const logout = () => {
    setUser(null);
    setIsAuthenticated(false);
    setIsPremium(false);
    navigate('/', { replace: true });
  };

  const upgradeToPremium = () => {
    if (user) {
      const updatedUser = { ...user, isPremium: true };
      updateUser(user.email, updatedUser);
      setUser(updatedUser);
      setIsPremium(true);
    }
  };

  const isEmailRegistered = (email: string) => {
    return email in users;
  };

  const updateUserProfile = async (data: Partial<User>) => {
    if (user) {
      const updatedUser = { ...user, ...data };
      updateUser(user.email, updatedUser);
      setUser(updatedUser);
    }
  };

  const value = {
    isAuthenticated,
    isPremium,
    user,
    login,
    register,
    logout,
    upgradeToPremium,
    isEmailRegistered,
    updateUserProfile
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}